import React, { useState } from 'react';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    isWeb,
    LabelRegular,
    ScrollView,
    View,
    YStack,
} from 'smoothie';

import type { User } from '../../../../user/types/User';
import type {
    AccountDetail,
    BankAccountDisplayDetail,
} from '../../../types/BankAccount';
import { type CardColor } from '../../../types/Card';
import { BatchOpenCardBankingTerms } from '../../BankingTerms';
import { CardAccountRow } from '../AccountsSection';
import { AutoPayDialog, AutoPaySection } from '../AutoPaySection';
import { PhysicalCardDetails } from '../CardDetailsSection';
import { RequestedCardType } from './CardPreference';

interface CardRequestDetailsProps {
    autopayEnabled: boolean;
    cardColor: CardColor;
    cardType: RequestedCardType | null;
    isRequestingPhysicalCard: boolean;
    onSubmit: (autopayEnabled: boolean) => void;
    preselectedAccountForCard: AccountDetail | BankAccountDisplayDetail | null;
    user: User;
}

const CardRequestDetails = ({
    autopayEnabled,
    cardColor,
    cardType,
    isRequestingPhysicalCard,
    onSubmit,
    preselectedAccountForCard,
    user,
}: CardRequestDetailsProps) => {
    const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
    const [enableAutoPay, setEnableAutoPay] = useState<boolean>(autopayEnabled);
    const [showAutoPayDialog, setShowAutoPayDialog] = useState<boolean>(false);

    return (
        <ScrollView
            contentContainerStyle={{
                flexGrow: 1,
                justifyContent: 'space-between',
            }}
        >
            <YStack
                gap={16}
                marginTop={isWeb ? 0 : 16}
                paddingHorizontal={4}
                marginBottom={50}
                $sm={{
                    paddingHorizontal: 36,
                }}
                $lg={{
                    paddingTop: 36,
                }}
                width="100%"
                height="100%"
                maxWidth={1140}
                justifyContent="space-between"
            >
                <View paddingVertical={24}>
                    <CardAccountRow
                        name={
                            preselectedAccountForCard?.nickname ??
                            'Fruitful account'
                        }
                        hasPhysicalCard={
                            cardType === RequestedCardType.VIRTUAL_AND_PHYSICAL
                        }
                        hasVirtualCard={true}
                        virtualCardColor={cardColor}
                    />
                </View>
                <YStack flex={1} width="100%" gap={16}>
                    <BatchOpenCardBankingTerms
                        termsAccepted={acceptTerms}
                        onPress={() => {
                            setAcceptTerms((oldVal) => {
                                return !oldVal;
                            });
                        }}
                        backgroundColor="$gray200"
                        padding={16}
                        borderRadius={16}
                    />
                    {isRequestingPhysicalCard && (
                        <PhysicalCardDetails user={user} />
                    )}
                    <AutoPaySection
                        isChecked={enableAutoPay}
                        onChange={() => {
                            setEnableAutoPay((oldValue) => {
                                return !oldValue;
                            });
                        }}
                        enabled={
                            preselectedAccountForCard?.autoPayEnabled == null
                        }
                        title="Enable AutoPay"
                        description="Enabling AutoPay means statement balances are paid off automatically each month. Without AutoPay, payments for all cards have to be made manually."
                    />
                    <Button
                        disabled={!acceptTerms}
                        text="Accept and submit"
                        size={ButtonSize.LARGE}
                        variant={ButtonVariant.PRIMARY}
                        onPress={() => {
                            // If the preselected account already has autopay setup, use that value because
                            // it was set by whoever opened the account.
                            if (
                                preselectedAccountForCard?.autoPayEnabled !=
                                null
                            ) {
                                onSubmit(enableAutoPay);
                            } else {
                                // Otherwise, assume that this member is setting it up and use whatever
                                // value they have selected.
                                if (enableAutoPay) {
                                    onSubmit(enableAutoPay);
                                } else {
                                    setShowAutoPayDialog(true);
                                }
                            }
                        }}
                    />
                    <YStack marginBottom={40} width="100%">
                        <LabelRegular color="$gray900">
                            Fruitful is a financial technology company, not a
                            bank. Deposit accounts provided by Emigrant Bank,
                            Member FDIC. The Fruitful Card is issued by Emigrant
                            Bank pursuant to license by Mastercard International
                            Incorporated.
                        </LabelRegular>
                    </YStack>
                </YStack>
            </YStack>
            <AutoPayDialog
                isOpen={showAutoPayDialog}
                onClose={() => {
                    setShowAutoPayDialog(false);
                }}
                onNoAutoPay={() => {
                    setShowAutoPayDialog(false);
                    setEnableAutoPay(false);
                    onSubmit(false);
                }}
                onYesAutoPay={() => {
                    setShowAutoPayDialog(false);
                    setEnableAutoPay(true);
                }}
            />
        </ScrollView>
    );
};

export default CardRequestDetails;
